import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';
import { fmtDateTimeLong } from '../../utils/dates.js';
import { useToast } from '../ui/Toast';

const STATUS_TABS = [
  { id: 'pending',  label: 'Pending review' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all',      label: 'All' },
];

export default function MidwichPriceSyncPage() {
  const { toast } = useToast();
  const [mailbox, setMailbox] = useState('');
  const [mailboxSaving, setMailboxSaving] = useState(false);

  const [runs, setRuns] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const [status, setStatus] = useState('pending');
  const [candidates, setCandidates] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadMailbox = useCallback(() => {
    api.get('app-settings/midwich_pricelist_mailbox').then(r => setMailbox(r?.value || '')).catch(() => {});
  }, []);
  const loadRuns = useCallback(() => {
    api.get('midwich/runs').then(r => setRuns(Array.isArray(r) ? r : [])).catch(() => setRuns([]));
  }, []);
  const loadCandidates = useCallback(() => {
    api.get('midwich/candidates', { status }).then(r => setCandidates(Array.isArray(r) ? r : [])).catch(() => setCandidates([]));
  }, [status]);

  useEffect(() => { loadMailbox(); loadRuns(); }, [loadMailbox, loadRuns]);
  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const saveMailbox = async () => {
    setMailboxSaving(true);
    try {
      await api.put('app-settings/midwich_pricelist_mailbox', { value: mailbox.trim() });
      toast.success('Mailbox saved.');
    } catch (e) { toast.error(e.message || 'Save failed'); }
    setMailboxSaving(false);
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post('midwich/sync');
      if (r.error) { toast.error(r.error); }
      else if (r.skipped) { toast.error(`Skipped: ${r.reason}`); }
      else { toast.success(`Checked ${r.checked} email${r.checked === 1 ? '' : 's'}, processed ${r.processed}.`); }
      loadRuns(); loadCandidates();
    } catch (e) { toast.error(e.message || 'Sync failed'); }
    setSyncing(false);
  };

  const approve = async (id) => {
    setBusyId(id);
    try { await api.post(`midwich/candidates/${id}/approve`); toast.success('Linked to catalogue product.'); loadCandidates(); }
    catch (e) { toast.error(e.message || 'Failed'); }
    setBusyId(null);
  };
  const reject = async (id) => {
    setBusyId(id);
    try { await api.post(`midwich/candidates/${id}/reject`); loadCandidates(); }
    catch (e) { toast.error(e.message || 'Failed'); }
    setBusyId(null);
  };

  return (
    <div className="fade-in space-y-4">
      <div>
        <h2 className="text-base font-extrabold text-navy">Midwich Price Sync</h2>
        <div className="text-[9px] text-gray-500">
          Midwich emails a full catalogue export once a day. This pulls it from the mailbox below, refreshes prices on
          catalogue items already linked to Midwich, flags exact part-number matches to un-linked items for review below,
          then deletes the email. The file's rows are kept as a searchable price list, replaced in full each morning, so
          the Midwich Lookup on the Product Catalog edit screen can find a part number and put its cost price on a
          product. Midwich have no live per-item API, so that retained list is the only way to price against them.
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded p-3">
        <div className="text-[8px] font-bold text-gray-500 uppercase mb-1">Mailbox that receives the daily file</div>
        <div className="flex items-center gap-2">
          <input className="form-input flex-1 max-w-xs" value={mailbox} onChange={e => setMailbox(e.target.value)} placeholder="pricing@altonvalley.com" />
          <button onClick={saveMailbox} disabled={mailboxSaving} className="px-2.5 py-1.5 text-[9px] font-semibold border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-40">
            {mailboxSaving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={syncNow} disabled={syncing} className="ml-auto px-3 py-1.5 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
            {syncing ? 'Syncing…' : '↺ Sync now'}
          </button>
        </div>
        <div className="text-[8px] text-gray-400 mt-1">Runs automatically every morning at 05:00 — use "Sync now" to check immediately.</div>
      </div>

      <div className="bg-white border border-gray-200 rounded">
        <div className="px-3 py-2 border-b border-gray-100 text-[9px] font-bold text-gray-500 uppercase">Recent import runs</div>
        {runs === null ? (
          <div className="text-[10px] text-gray-400 py-8 text-center">Loading…</div>
        ) : runs.length === 0 ? (
          <div className="text-[10px] text-gray-400 py-8 text-center">No Midwich emails processed yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[9px]">
              <thead>
                <tr className="text-left text-gray-400 uppercase text-[7px]">
                  <th className="px-3 py-1.5">Processed</th>
                  <th className="px-3 py-1.5">Subject</th>
                  <th className="px-3 py-1.5 text-right">Rows</th>
                  <th className="px-3 py-1.5 text-right" title="Lines the parser could not read, so they are not in the price list. Should be 0.">Skipped</th>
                  <th className="px-3 py-1.5 text-right" title="Rows held in the searchable Midwich price list after this run.">In price list</th>
                  <th className="px-3 py-1.5 text-right" title="SKUs dropped because they were not in this file.">Withdrawn</th>
                  <th className="px-3 py-1.5 text-right">Price updated</th>
                  <th className="px-3 py-1.5 text-right">New candidates</th>
                  <th className="px-3 py-1.5 text-right">Unmatched</th>
                  <th className="px-3 py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} className="border-t border-gray-50">
                    <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">{fmtDateTimeLong(r.processed_at)}</td>
                    <td className="px-3 py-1.5 truncate max-w-xs">{r.subject}</td>
                    <td className="px-3 py-1.5 text-right">{r.rows_total ?? '—'}</td>
                    <td className={`px-3 py-1.5 text-right ${r.rows_skipped > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>{r.rows_skipped ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right">{r.rows_retained ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right text-gray-400">{r.rows_withdrawn ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right">{r.rows_price_updated ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right">{r.rows_new_candidates ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right text-gray-400">{r.rows_unmatched ?? '—'}</td>
                    <td className="px-3 py-1.5">
                      {r.status === 'success'
                        ? <span className="text-emerald-600 font-semibold">✓ ok</span>
                        : <span className="text-red-600 font-semibold" title={r.error_message || ''}>✕ error</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded">
        <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2">
          <span className="text-[9px] font-bold text-gray-500 uppercase">Catalogue match candidates</span>
          <div className="ml-auto flex border border-gray-200 rounded overflow-hidden">
            {STATUS_TABS.map(t => (
              <button key={t.id} onClick={() => setStatus(t.id)}
                className={`px-2.5 py-1 text-[8px] font-semibold ${status === t.id ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{t.label}</button>
            ))}
          </div>
        </div>
        <div className="text-[8px] text-gray-400 px-3 pt-2">
          Midwich SKUs whose manufacturer part number exactly matches a product we already carry, but that isn't linked to
          Midwich as a supplier yet. Approving creates the supplier link (with auto-sync on) so future daily files keep its price fresh.
        </div>
        {candidates === null ? (
          <div className="text-[10px] text-gray-400 py-8 text-center">Loading…</div>
        ) : candidates.length === 0 ? (
          <div className="text-[10px] text-gray-400 py-8 text-center">Nothing here.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[9px]">
              <thead>
                <tr className="text-left text-gray-400 uppercase text-[7px]">
                  <th className="px-3 py-1.5">Midwich SKU</th>
                  <th className="px-3 py-1.5">Description</th>
                  <th className="px-3 py-1.5">Matched catalogue product</th>
                  <th className="px-3 py-1.5 text-right">Cost price</th>
                  <th className="px-3 py-1.5 text-right">Stock</th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {candidates.map(c => (
                  <tr key={c.id} className="border-t border-gray-50">
                    <td className="px-3 py-1.5 font-mono">{c.midwich_sku}</td>
                    <td className="px-3 py-1.5 truncate max-w-xs" title={c.description}>{c.description}</td>
                    <td className="px-3 py-1.5">{c.candidate_code} — {c.candidate_description}</td>
                    <td className="px-3 py-1.5 text-right">{formatMoney(c.cost_price, 2)}</td>
                    <td className="px-3 py-1.5 text-right text-gray-500">{c.stock_qty}</td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {c.review_status === 'pending' ? (
                        <>
                          <button onClick={() => approve(c.id)} disabled={busyId === c.id}
                            className="px-2 py-0.5 text-[8px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 mr-1">Approve</button>
                          <button onClick={() => reject(c.id)} disabled={busyId === c.id}
                            className="px-2 py-0.5 text-[8px] font-semibold border border-gray-300 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-40">Reject</button>
                        </>
                      ) : (
                        <span className={`text-[8px] font-semibold ${c.review_status === 'approved' ? 'text-emerald-600' : 'text-gray-400'}`}>
                          {c.review_status === 'approved' ? '✓ approved' : '✕ rejected'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
